import { useState, useEffect, ChangeEvent } from 'react';
import { Accordion, Input, RadioButtons } from '@ds';
import { useUserProfileStore } from 'src/stores/UserProfile';
import { ContactEmail } from 'src/shared/types/User';
import { InputChangeVariant, EmailRegex } from '@design-system/elements/Input/Input.types';
import { processUpdatedDetails } from 'src/pages/UserProfile/UserProfile.service';

interface IEmails {
  contactRefId: number;
  emails: ContactEmail[];
}

const INITIAL_EMAIL: ContactEmail = {
  emailAddress: '',
  emailId: undefined,
  isPrimary: false,
};

const Emails = (props: IEmails) => {
  const { emails, contactRefId } = props;
  const { editMode, updatedFields, setUpdatedFields: setUpdatedFields } = useUserProfileStore();
  const [contactEmails, setContactEmails] = useState<ContactEmail[]>([{ ...INITIAL_EMAIL }]);
  const [activeIndexes, setActiveIndexes] = useState<number[]>([0]);
  // const isRequired = !!(currentProfileSelection.isPrincipal && userProfile['webAccess']?.enabled);

  useEffect(() => {
    if (emails?.length > 0) {
      setContactEmails(JSON.parse(JSON.stringify(emails)));
      setActiveIndexes(Array.from({ length: emails.length }, (_, index) => index));
    } else {
      setContactEmails([INITIAL_EMAIL]);
      setActiveIndexes([0]);
    }
  }, [emails]);

  useEffect(() => {
    if (editMode) {
      contactEmails.forEach((item) => {
        item.isValid = EmailRegex.test(item.emailAddress);
      });
      const updatedEmails = processUpdatedDetails(
        contactEmails,
        emails.map((email) => ({ ...email })),
        { contactRefId },
        'emailId',
        ['emailAddress']
      );
      const newFields = { ...updatedFields };
      const hasUpdatedEmails = Object.values(updatedEmails).some((list) => list?.length > 0);
      if (hasUpdatedEmails) {
        newFields.contactEmails = updatedEmails;
      } else {
        delete newFields.contactEmails;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, contactRefId, emails, contactEmails]);

  const handleAddAnother = () => {
    setActiveIndexes([...activeIndexes, contactEmails.length]);
    setContactEmails((prevState) => [...prevState, { ...INITIAL_EMAIL }]);
  };

  const handleDeleteTab = async (index: number) => {
    const updatedEmails = [...contactEmails];
    updatedEmails.splice(index, 1);
    const emailObj = contactEmails[index];
    const isPrimary = emailObj.isPrimary && updatedEmails.length > 0;

    if (isPrimary) updatedEmails[0].isPrimary = true;
    setContactEmails(updatedEmails);

    if (updatedEmails.length === 1) {
      setActiveIndexes([0]);
    }
  };

  const handleEmailChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const newEmails = [...contactEmails];
    newEmails[index] = { ...newEmails[index], emailAddress: event.target.value };
    setContactEmails(newEmails);
  };

  const handleIsPrimaryChange = (index: number) => {
    const newEmails = contactEmails.map((contact, emailIndex) => ({ ...contact, isPrimary: emailIndex === index }));
    setContactEmails(newEmails);
  };

  const contactEmailsTabs = contactEmails.map((item, index) => {
    const { isPrimary, emailAddress, emailId } = item;
    const refEmail = emails?.find((email) => email.emailId === emailId);

    return {
      body: (
        <div className="w-full flex flex-col gap-[16px] md:flex-row">
          <div className="flex items-center lg:w-1/2 md:w-5/6 sm:w-full ">
            <div className="flex-1">
              <Input
                required={true}
                label="Email"
                type="email"
                value={emailAddress || ''}
                onChange={(event) => handleEmailChange(index, event)}
                hasChanged={!!refEmail && (emailAddress || '') !== (refEmail.emailAddress || '')}
                // required={isRequired}
                disabled={!editMode}
                inputChangedStyle={InputChangeVariant.Secondary}
              />
            </div>
          </div>
          <div className="flex items-center gap-[16px]">
            <RadioButtons
              name="emailPrimary"
              onChange={() => handleIsPrimaryChange(index)}
              selectedOption={isPrimary ? index : ''}
              options={[{ label: 'Primary', value: index }]}
              type={!refEmail?.isPrimary == !isPrimary ? 'secondary' : 'highlighted'}
              disabled={!editMode}
            />
          </div>
        </div>
      ),
    };
  });

  const disableAdd =
    !editMode || contactEmails.some((email) => !email.emailAddress || !EmailRegex.test(email.emailAddress));

  return (
    <Accordion
      multiple
      allowToDelete={true}
      isDeleteDisabled={!editMode}
      shouldPreserveFirstTab
      tabs={contactEmailsTabs}
      activeIndex={activeIndexes}
      onTabChange={({ index }) => setActiveIndexes(index as number[])}
      onDeleteTab={handleDeleteTab}
      tabHeaderPrefix="Email"
      additionalTabConfig={{
        buttonLabel: 'EMAILS',
        onAdd: handleAddAnother,
        disableAdd,
      }}
    />
  );
};

export default Emails;
